export type TextMeasurer = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: number
) => number;

export interface WrapOptions {
  minLines: number;
  maxLines: number;
  maxWidth: number;
  minFontSize: number;
  maxFontSize: number;
  wordGap: number;
  fontFamily: string;
  fontWeight: number;
  measure: TextMeasurer;
}

export interface FitOptions extends WrapOptions {
  maxHeight: number;
  lineHeightRatio: number;
}

export interface FitLine {
  words: string[];
  fontSize: number;
  width: number;
}

export interface FitLayout {
  lines: FitLine[];
  totalHeight: number;
  overflow: boolean;
}

export const isHighlightedWord = (word: string): boolean => {
  const cleaned = word.trim().replace(/^[^A-Za-z]+|[^A-Za-z]+$/g, '');
  return cleaned.length > 0 && cleaned === cleaned.toUpperCase() && /[A-Z]/.test(cleaned);
};

export const splitManualLines = (text: string): string[][] =>
  text
    .split(/[|\n]+/)
    .map((line) => words(line))
    .filter((line) => line.length > 0);

export const wrapBalancedLines = (text: string, options: WrapOptions): string[][] => {
  const allWords = words(text);
  if (allWords.length === 0) return [];

  const minLines = Math.max(1, Math.min(options.minLines, allWords.length));
  const maxLines = Math.max(minLines, Math.min(options.maxLines, allWords.length));
  const candidates: string[][][] = [];

  for (let lineCount = minLines; lineCount <= maxLines; lineCount += 1) {
    candidates.push(...partitions(allWords, lineCount));
  }

  return candidates
    .map((candidate) => ({ candidate, score: scoreCandidate(candidate, options) }))
    .sort((left, right) => right.score - left.score)[0]?.candidate ?? [allWords];
};

export const fitTextBlock = (text: string, options: FitOptions): FitLayout => {
  const lines = /[|\n]/.test(text) ? splitManualLines(text) : wrapBalancedLines(text, options);
  let fitted = lines.map((line) => fitLine(line, options));

  const initialHeight = totalHeight(fitted, options.lineHeightRatio);
  if (initialHeight > options.maxHeight && initialHeight > 0) {
    const scale = options.maxHeight / initialHeight;
    fitted = fitted.map((line) => {
      const fontSize = Math.max(options.minFontSize, Math.floor(line.fontSize * scale));
      return {
        ...line,
        fontSize,
        width: measureLine(line.words, fontSize, options),
      };
    });
  }

  return {
    lines: fitted,
    totalHeight: totalHeight(fitted, options.lineHeightRatio),
    overflow:
      fitted.some((line) => line.width > options.maxWidth) ||
      totalHeight(fitted, options.lineHeightRatio) > options.maxHeight,
  };
};

export const createCanvasMeasurer = (): TextMeasurer => {
  if (typeof document === 'undefined') {
    return fallbackMeasure;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return fallbackMeasure;

  return (text, fontSize, fontFamily, fontWeight) => {
    context.font = `${fontWeight} ${fontSize}px "${fontFamily}"`;
    return context.measureText(text).width;
  };
};

export const wrapBodyText = (
  text: string,
  maxWidth: number,
  fontSize: number,
  fontFamily: string,
  fontWeight: number,
  measure: TextMeasurer
): string[] => {
  const result: string[] = [];
  const manualLines = text.split(/[|\n]+/).map((line) => line.trim()).filter(Boolean);
  const sourceLines = manualLines.length > 0 ? manualLines : [text];

  sourceLines.forEach((sourceLine) => {
    let current = '';
    words(sourceLine).forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (current && measure(candidate, fontSize, fontFamily, fontWeight) > maxWidth) {
        result.push(current);
        current = word;
      } else {
        current = candidate;
      }
    });
    if (current) result.push(current);
  });

  return result;
};

export const fitBodyText = (
  text: string,
  maxWidth: number,
  maxHeight: number,
  minFontSize: number,
  maxFontSize: number,
  lineHeightRatio: number,
  fontFamily: string,
  fontWeight: number,
  measure: TextMeasurer
): { lines: string[]; fontSize: number; lineHeight: number; overflow: boolean } => {
  let low = minFontSize;
  let high = maxFontSize;
  let best = minFontSize;
  let bestLines = wrapBodyText(text, maxWidth, minFontSize, fontFamily, fontWeight, measure);

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const lines = wrapBodyText(text, maxWidth, mid, fontFamily, fontWeight, measure);
    const lineHeight = Math.round(mid * lineHeightRatio);
    const height = lines.length * lineHeight;
    if (height <= maxHeight && lines.every((line) => measure(line, mid, fontFamily, fontWeight) <= maxWidth)) {
      best = mid;
      bestLines = lines;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  const lineHeight = Math.round(best * lineHeightRatio);
  return {
    lines: bestLines,
    fontSize: best,
    lineHeight,
    overflow: bestLines.length * lineHeight > maxHeight,
  };
};

const fitLine = (line: string[], options: WrapOptions): FitLine => {
  let low = options.minFontSize;
  let high = options.maxFontSize;
  let best = low;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (measureLine(line, mid, options) <= options.maxWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return {
    words: line,
    fontSize: best,
    width: measureLine(line, best, options),
  };
};

const scoreCandidate = (candidate: string[][], options: WrapOptions): number => {
  const fitted = candidate.map((line) => fitLine(line, options));
  const minFont = Math.min(...fitted.map((line) => line.fontSize));
  const widths = fitted.map((line) => line.width / options.maxWidth);
  const averageWidth = widths.reduce((sum, width) => sum + width, 0) / widths.length;
  const variance = widths.reduce((sum, width) => sum + Math.abs(width - averageWidth), 0);
  const overflowPenalty = fitted.some((line) => line.width > options.maxWidth) ? 1000 : 0;
  return minFont * 100 - variance * 30 - candidate.length * 4 - overflowPenalty;
};

const measureLine = (line: string[], fontSize: number, options: WrapOptions): number => {
  const wordsWidth = line.reduce(
    (sum, word) =>
      sum + options.measure(word.toUpperCase(), fontSize, options.fontFamily, options.fontWeight),
    0
  );
  return wordsWidth + Math.max(0, line.length - 1) * options.wordGap;
};

const partitions = (allWords: string[], lineCount: number): string[][][] => {
  if (lineCount === 1) return [[allWords]];

  const results: string[][][] = [];
  const maxFirstLineLength = allWords.length - lineCount + 1;

  for (let firstLineLength = 1; firstLineLength <= maxFirstLineLength; firstLineLength += 1) {
    const first = allWords.slice(0, firstLineLength);
    const rest = allWords.slice(firstLineLength);
    partitions(rest, lineCount - 1).forEach((tail) => {
      results.push([first, ...tail]);
    });
  }

  return results;
};

const totalHeight = (lines: FitLine[], lineHeightRatio: number): number =>
  lines.reduce((sum, line) => sum + line.fontSize * lineHeightRatio, 0);

const words = (text: string): string[] => text.trim().split(/\s+/).filter(Boolean);

const fallbackMeasure: TextMeasurer = (text, fontSize) => text.length * fontSize * 0.56;
